/**
 * Content Review Service
 *
 * Frontend API client for the Content Review Dashboard (Story 29).
 * Provides access to pending reviews, metrics, queue, and approval workflows.
 *
 * Date: December 16, 2025
 */

import { authFetch } from '@/utils/auth-fetch';

// Types
export interface PendingItem {
  id: string;
  title: string;
  contentType: 'ai_article' | 'product_aeo' | 'category_aeo';
  createdAt: string;
  siteName: string;
}

export interface PendingResponse {
  success: boolean;
  data: {
    totalPending: number;
    items: PendingItem[];
  };
}

export interface ReviewMetrics {
  totalPending: number;
  reviewedInPeriod: number;
  newInPeriod: number;
  approvalRate: number;
  rejectionRate: number;
  approvedCount: number;
  rejectedCount: number;
  byContentType: {
    ai_article: number;
    product_aeo: number;
    category_aeo: number;
  };
  topReviewers: Array<{
    userId: string;
    name: string;
    count: number;
  }>;
}

export interface MetricsResponse {
  success: boolean;
  data: ReviewMetrics;
}

export interface PreviousRejection {
  reason: string;
  rejectedAt: string;
  rejectedBy: string | null;
  totalRejections: number;
}

export interface QueueItem {
  id: string;
  title: string;
  contentType: 'ai_article' | 'product_aeo' | 'category_aeo';
  status: 'pending' | 'approved' | 'rejected' | 'needs_research';
  createdAt: string;
  updatedAt: string;
  siteId: string;
  siteName: string;
  approvedBy: string | null;
  reviewerName: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  faqQaPassed: boolean | null;
  faqCount: number | null;
  // Previous rejection info (for items that were regenerated after rejection)
  previousRejection: PreviousRejection | null;
  // Needs manual review (e.g., suspicious data flagged for human verification)
  needsManualReview: boolean;
  // Product category (only for product_aeo)
  category: string | null;
  // Is inactive: product no longer in active feed (only for product_aeo)
  isInactive?: boolean;
  // Product image URL for queue thumbnail (product_aeo only; null for other types)
  imageUrl?: string | null;
}

export interface QueueFilters {
  siteId?: string | null;
  contentType?: string | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  reviewerId?: string | null;
  faqQaPassed?: boolean | null;
  hasPreviousRejection?: boolean | null; // Filter for items with rejection history
  needsManualReview?: boolean | null; // Filter for items flagged for manual review (e.g., suspicious data)
  includeInactive?: boolean; // Include products no longer in active feed (for managing legacy AEO)
  includePublished?: boolean; // Include published content in results (for reviewing what's live)
  search?: string | null; // Search by product name or product ID
  urlContains?: string | null; // Substring match on product URL (paste a product URL or its embedded ID); product_aeo only
  category?: string | null; // Filter by product category path
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

export interface QueueResponse {
  success: boolean;
  data: {
    items: QueueItem[];
    total: number;
    page: number;
    totalPages: number;
    limit: number;
  };
}

export interface AvailableLanguage {
  language: string;
  isSource: boolean;
  available: boolean;
}

export interface ContentDetails {
  id: string;
  contentType: 'ai_article' | 'product_aeo' | 'category_aeo';
  siteId: string;
  siteName: string;
  createdAt: string;
  updatedAt: string;
  approvedBy: string | null;
  reviewerName: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  title: string;
  content?: string;
  contentHtml?: string;
  metadata?: Record<string, unknown>;
  status: string;
  // product_aeo only — language selector context
  currentLanguage?: string;
  sourceLanguage?: string;
  availableLanguages?: AvailableLanguage[];
  expectedSectionKeys?: string[];
  [key: string]: unknown;
}

export interface ContentDetailsResponse {
  success: boolean;
  data: ContentDetails;
}

export interface ApprovalResult {
  success: boolean;
  data: {
    id: string;
    contentType: string;
    reviewedBy: string;
    reviewedAt: string;
    status: string;
  };
}

export interface RejectionResult {
  success: boolean;
  data: {
    id: string;
    contentType: string;
    reviewedBy: string;
    reviewedAt: string;
    status: string;
    reason: string;
  };
}

export interface JSONLDResponse {
  success: boolean;
  data: Record<string, unknown>;
}

// AI Analysis Types
export interface RecommendedSolution {
  type: 'domain_knowledge' | 'data_fix' | 'code_bug';
  reason: string;
  knowledge_type?: 'terminology' | 'specification' | 'context' | 'expertise_signal' | 'validation_rule';
  suggested_scope?: {
    level: 'category' | 'brand' | 'global';
    value: string | null;
  };
  estimated_impact?: number;
}

export interface ContentIssue {
  type: 'TERMINOLOGY' | 'SPECIFICATION' | 'MISSING_CONTEXT' | 'EXPERTISE_SIGNAL' | 'HALLUCINATION' | 'LOGIC_ERROR';
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  location: string;
  suggestion: string;
  recommended_solution?: RecommendedSolution;
}

export interface KnowledgeGapSuggestion {
  gap: string;
  scope: 'SECTOR' | 'CATEGORY' | 'BRAND' | 'ATTRIBUTE';
  scopeValue: string;
  researchQuestion: string;
  proposedKnowledge: string;
}

export interface ContentAnalysis {
  QUALITY_SCORE: number;
  RECOMMENDATION: 'APPROVE' | 'APPROVE_WITH_NOTES' | 'REJECT';
  SUMMARY: string;
  STRENGTHS: string[];
  ISSUES_FOUND: ContentIssue[];
  KNOWLEDGE_GAPS: KnowledgeGapSuggestion[];
  REVIEWER_NOTES: string[];
  CONFIDENCE: 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface AnalysisResponse {
  success: boolean;
  contentId: string;
  contentType: string;
  analysis: ContentAnalysis;
  proposal?: {
    title: string;
    knowledgeType: string;
    knowledgeText: string;
    scopeLevel: string;
    scopeValue: string | null;
    researchQuestion: string;
    researchMethodology: string;
    gapAnalysis: Record<string, unknown>;
    allIssues: ContentIssue[];
    allKnowledgeGaps: KnowledgeGapSuggestion[];
  };
}

export interface KnowledgeProposalResult {
  success: boolean;
  feedback: {
    id: string;
    contentType: string;
    contentId: string;
  };
  knowledgeProposals: Array<{
    id: string;
    title: string;
    knowledgeType: string;
    scopeLevel: string;
    status: string;
  }>;
}

// API Functions
export async function getPendingItems(
  siteId?: string | null,
  limit: number = 5
): Promise<PendingResponse> {
  const params = new URLSearchParams();
  if (siteId) params.set('siteId', siteId);
  params.set('limit', limit.toString());

  const response = await authFetch(`/content-review/pending?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch pending items');
  }

  return response.json();
}

export async function getMetrics(filters?: {
  siteId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}): Promise<MetricsResponse> {
  const params = new URLSearchParams();
  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);

  const response = await authFetch(`/content-review/metrics?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch metrics');
  }

  return response.json();
}

export async function getQueue(filters?: QueueFilters): Promise<QueueResponse> {
  const params = new URLSearchParams();

  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.contentType) params.set('contentType', filters.contentType);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.reviewerId) params.set('reviewerId', filters.reviewerId);
  if (filters?.faqQaPassed !== undefined && filters?.faqQaPassed !== null) {
    params.set('faqQaPassed', filters.faqQaPassed.toString());
  }
  if (filters?.hasPreviousRejection !== undefined && filters?.hasPreviousRejection !== null) {
    params.set('hasPreviousRejection', filters.hasPreviousRejection.toString());
  }
  if (filters?.needsManualReview !== undefined && filters?.needsManualReview !== null) {
    params.set('needsManualReview', filters.needsManualReview.toString());
  }
  if (filters?.includeInactive) {
    params.set('includeInactive', 'true');
  }
  if (filters?.includePublished) {
    params.set('includePublished', 'true');
  }
  if (filters?.search) params.set('search', filters.search);
  if (filters?.urlContains) params.set('urlContains', filters.urlContains);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.page) params.set('page', filters.page.toString());
  if (filters?.limit) params.set('limit', filters.limit.toString());
  if (filters?.sortBy) params.set('sortBy', filters.sortBy);
  if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder);

  // Add cache-busting parameter to prevent browser caching
  params.set('_t', Date.now().toString());

  const response = await authFetch(`/content-review/queue?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch queue');
  }

  return response.json();
}

export interface QueueIdsResponse {
  success: boolean;
  data: {
    ids: Array<{ id: string; contentType: string }>;
    total: number;
    truncated: boolean;
  };
}

/**
 * Get all queue item IDs matching filters (lightweight endpoint for "Select All")
 * Returns only id and contentType to minimize data transfer
 */
export async function getQueueIds(filters?: Omit<QueueFilters, 'page' | 'limit' | 'sortBy' | 'sortOrder'>): Promise<QueueIdsResponse> {
  const params = new URLSearchParams();

  if (filters?.siteId) params.set('siteId', filters.siteId);
  if (filters?.contentType) params.set('contentType', filters.contentType);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.startDate) params.set('startDate', filters.startDate);
  if (filters?.endDate) params.set('endDate', filters.endDate);
  if (filters?.reviewerId) params.set('reviewerId', filters.reviewerId);
  if (filters?.faqQaPassed !== undefined && filters?.faqQaPassed !== null) {
    params.set('faqQaPassed', filters.faqQaPassed.toString());
  }
  if (filters?.hasPreviousRejection !== undefined && filters?.hasPreviousRejection !== null) {
    params.set('hasPreviousRejection', filters.hasPreviousRejection.toString());
  }
  if (filters?.needsManualReview !== undefined && filters?.needsManualReview !== null) {
    params.set('needsManualReview', filters.needsManualReview.toString());
  }
  if (filters?.includeInactive) {
    params.set('includeInactive', 'true');
  }
  if (filters?.includePublished) {
    params.set('includePublished', 'true');
  }
  if (filters?.search) params.set('search', filters.search);
  if (filters?.urlContains) params.set('urlContains', filters.urlContains);
  if (filters?.category) params.set('category', filters.category);

  const response = await authFetch(`/content-review/queue/ids?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch queue IDs');
  }

  return response.json();
}

export interface CategoryWithCount {
  category: string;
  productCount: number;
}

export interface CategoriesResponse {
  success: boolean;
  data: CategoryWithCount[];
}

/**
 * Get categories that have products with AEO content for the filter dropdown
 */
export async function getProductCategories(siteId: string): Promise<CategoriesResponse> {
  const response = await authFetch(`/content-review/product-categories-with-aeo/${siteId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch categories');
  }

  return response.json();
}

export async function getContentDetails(
  contentType: string,
  contentId: string,
  siteId: string,
  language?: string | null
): Promise<ContentDetailsResponse> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content access');
  }
  // Add timestamp to prevent browser caching (query param only - headers cause CORS issues)
  const timestamp = Date.now();
  const langParam = language ? `&language=${encodeURIComponent(language)}` : '';
  const response = await authFetch(`/content-review/${contentType}/${contentId}?siteId=${siteId}${langParam}&_t=${timestamp}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch content details');
  }

  return response.json();
}

export async function approveContent(
  contentType: string,
  contentId: string,
  siteId: string,
  notes?: string
): Promise<ApprovalResult> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content approval');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes, siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to approve content');
  }

  return response.json();
}

export async function rejectContent(
  contentType: string,
  contentId: string,
  siteId: string,
  reason: string,
  flagAsDataIssue: boolean = false
): Promise<RejectionResult> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content rejection');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, siteId, flagAsDataIssue }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject content');
  }

  return response.json();
}

export async function updateRejectionReason(
  contentType: string,
  contentId: string,
  siteId: string,
  reason: string
): Promise<{ success: boolean; data: { id: string; contentType: string; rejectionReason: string; previousReason: string | null } }> {
  if (!siteId) {
    throw new Error('Site ID is required for rejection reason update');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/rejection-reason`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason, siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update rejection reason');
  }

  return response.json();
}

export async function resetToPending(
  contentType: string,
  contentId: string,
  siteId: string
): Promise<{ success: boolean; data: { id: string; contentType: string; status: string } }> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content reset');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reset content to pending');
  }

  return response.json();
}

// Review History Types
export interface ReviewHistoryItem {
  id: string;
  action: 'approved' | 'rejected' | 'reset' | 'needs_research' | 'rejection_edited';
  reason: string | null;
  reviewedAt: string;
  contentVersion: number;
  metadata: Record<string, unknown>;
  reviewerName: string | null;
  reviewerEmail: string | null;
}

export interface ReviewHistoryResponse {
  success: boolean;
  data: {
    items: ReviewHistoryItem[];
    total: number;
  };
}

/**
 * Get full review history for a content item
 * Shows all approval/rejection/reset actions over time
 */
export async function getReviewHistory(
  contentType: string,
  contentId: string
): Promise<ReviewHistoryResponse> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/history`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get review history');
  }

  return response.json();
}

export interface BulkApproveItem {
  contentType: string;
  id: string;
}

export interface BulkApproveResult {
  success: boolean;
  approved: BulkApproveItem[];
  failed: Array<BulkApproveItem & { error: string }>;
  totalRequested: number;
  message: string;
  reviewerId: string;
}

// Bulk Regeneration Types
export interface BulkRegenerateItem {
  contentType: string;
  id: string;
}

export interface BulkRegenerateBatchResult {
  success: boolean;
  mode: 'batch';
  runId: string;
  totalProducts: number;
  configuredModel?: string;
  batchProvider?: 'openai' | 'vertexai';
  message: string;
  statusUrl: string;
}

export interface BulkRegenerateRealtimeResult {
  success: boolean;
  mode: 'realtime';
  totalProducts: number;
  regenerated: number;
  failed: number;
  message: string;
  details: Array<{ productId: string; status: string; error?: string }>;
}

export type BulkRegenerateResult = BulkRegenerateBatchResult | BulkRegenerateRealtimeResult;

/**
 * Bulk approve multiple content items
 * The current user becomes the reviewer for all approved items (for citation)
 */
export async function bulkApprove(
  items: BulkApproveItem[],
  siteId: string,
  notes?: string
): Promise<BulkApproveResult> {
  if (!siteId) {
    throw new Error('Site ID is required for bulk approval');
  }
  const response = await authFetch('/content-review/bulk-approve', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, siteId, notes }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to bulk approve content');
  }

  return response.json();
}

/**
 * Bulk regenerate multiple content items
 * Uses either batch mode (SubBatchProcessorService) or real-time mode (AEOProductPageService)
 *
 * @param items - Array of items to regenerate (must have contentType and id)
 * @param mode - 'batch' for cost-efficient batch API (~50% savings, ~24h), 'realtime' for immediate (max 10 items)
 * @param siteId - Site ID for multi-tenancy
 */
export async function bulkRegenerate(
  items: BulkRegenerateItem[],
  mode: 'batch' | 'realtime',
  siteId: string
): Promise<BulkRegenerateResult> {
  if (!siteId) {
    throw new Error('Site ID is required for bulk regeneration');
  }

  const response = await authFetch('/content-review/bulk-regenerate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, mode, siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to bulk regenerate content');
  }

  return response.json();
}

/**
 * Regenerate result types
 */
export interface RegenerateResult {
  success: boolean;
  async?: boolean;
  jobId?: string;
  message?: string;
  error?: string;
  generationId?: string;
}

export interface JobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: {
    percent: number;
    message: string;
  };
  result?: unknown;
  error?: string;
}

/**
 * Get job status (for polling async operations)
 */
export async function getJobStatus(jobId: string): Promise<JobStatus> {
  const response = await authFetch(`/jobs/${jobId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get job status');
  }

  return response.json();
}

/**
 * Regenerate content (currently only supports product_aeo)
 * Returns immediately with job ID for async polling (default)
 * Use onProgress callback to track progress
 */
export async function regenerateContent(
  contentType: string,
  contentId: string,
  options?: {
    language?: string;
    async?: boolean;
    onProgress?: (progress: { phase: string; percent: number }) => void;
  }
): Promise<RegenerateResult> {
  const { language, async: useAsync = true, onProgress } = options || {};

  const response = await authFetch(`/content-review/${contentType}/${contentId}/regenerate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ language, async: useAsync }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to regenerate content');
  }

  const result = await response.json();

  // If async mode, poll for completion
  if (result.async && result.jobId) {
    onProgress?.({ phase: 'Processing...', percent: 10 });

    return new Promise((resolve, reject) => {
      const pollInterval = setInterval(async () => {
        try {
          const jobStatus = await getJobStatus(result.jobId);

          if (jobStatus.status === 'processing') {
            // Read progress from the backend's { percent, message } object structure
            const progressPercent = jobStatus.progress?.percent;
            const progressNum = typeof progressPercent === 'number' && !isNaN(progressPercent) ? progressPercent : 0;
            const percent = Math.min(90, 10 + progressNum * 0.8);
            const phase = jobStatus.progress?.message || 'Generating content...';
            onProgress?.({ phase, percent });
          } else if (jobStatus.status === 'completed') {
            clearInterval(pollInterval);
            onProgress?.({ phase: 'Complete', percent: 100 });
            resolve({
              success: true,
              message: 'AEO content regenerated successfully',
              generationId: contentId
            });
          } else if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            reject(new Error(jobStatus.error || 'Regeneration failed'));
          }
        } catch (pollError) {
          clearInterval(pollInterval);
          reject(pollError);
        }
      }, 3000); // Poll every 3 seconds

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        reject(new Error('Regeneration timed out'));
      }, 300000);
    });
  }

  return result;
}

export async function getContentJSONLD(
  contentType: string,
  contentId: string
): Promise<JSONLDResponse> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/jsonld`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch JSON-LD');
  }

  return response.json();
}

// Helper function to get content type display name
export function getContentTypeLabel(contentType: string): string {
  switch (contentType) {
    case 'ai_article':
      return 'AI Article';
    case 'product_aeo':
      return 'Product AEO';
    case 'category_aeo':
      return 'Category AEO';
    default:
      return contentType;
  }
}

// Helper function to get status display name
export function getStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Pending Review';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'needs_research':
      return 'Needs Research';
    default:
      return status;
  }
}

// AI Analysis Functions

/**
 * Analyze content with AI (LLM-as-judge) for review assistance
 * Returns quality assessment, issues, and knowledge gap suggestions
 */
export async function analyzeContent(
  contentType: string,
  contentId: string,
  siteId: string
): Promise<AnalysisResponse> {
  // CRITICAL: siteId required for multi-tenancy security
  if (!siteId) {
    throw new Error('Site ID is required for content analysis');
  }
  const response = await authFetch(`/content-review/${contentType}/${contentId}/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to analyze content');
  }

  return response.json();
}

/**
 * Create feedback entry and knowledge proposals from AI analysis
 * Links the analysis to domain knowledge for future improvement
 */
export async function createKnowledgeProposals(
  contentType: string,
  contentId: string,
  analysis: ContentAnalysis,
  reviewerComment?: string
): Promise<KnowledgeProposalResult> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/analyze/create-proposals`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ analysis, reviewerComment }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create knowledge proposals');
  }

  return response.json();
}

// Helper function to get issue type label
export function getIssueTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    TERMINOLOGY: 'Terminology',
    SPECIFICATION: 'Specification',
    MISSING_CONTEXT: 'Missing Context',
    EXPERTISE_SIGNAL: 'Expertise Signal',
    HALLUCINATION: 'Hallucination',
    LOGIC_ERROR: 'Logic Error',
  };
  return labels[type] || type;
}

// Helper function to get severity color
export function getSeverityColor(severity: string): string {
  switch (severity) {
    case 'HIGH':
      return 'destructive';
    case 'MEDIUM':
      return 'warning';
    case 'LOW':
      return 'secondary';
    default:
      return 'secondary';
  }
}

// Helper function to get recommendation color
export function getRecommendationColor(recommendation: string): 'default' | 'destructive' | 'secondary' {
  switch (recommendation) {
    case 'APPROVE':
      return 'default';
    case 'APPROVE_WITH_NOTES':
      return 'secondary';
    case 'REJECT':
      return 'destructive';
    default:
      return 'secondary';
  }
}

// ============================================
// Evaluation Persistence and Actions
// ============================================

export type ActionType = 'SEND_SUGGESTION' | 'CREATE_DATA_ISSUE' | 'TRIGGER_RESEARCH' | 'FLAG_BUG' | 'DISMISS';
export type ActionStatus = 'pending' | 'in_progress' | 'completed' | 'dismissed';

export interface ImprovementAction {
  id: string;
  evaluationId: string;
  itemType: 'issue' | 'gap';
  itemIndex: number;
  actionType: ActionType;
  actionStatus: ActionStatus;
  referenceType?: string;
  referenceId?: string;
  notes?: string;
  createdBy?: string;
  createdByName?: string;
  completedBy?: string;
  completedByName?: string;
  completedAt?: string;
  createdAt: string;
}

export interface SavedEvaluation {
  id: string;
  siteId: string;
  contentType: string;
  contentId: string;
  qualityScore: number;
  recommendation: string;
  confidence: string;
  summary: string;
  strengths: string[];
  issuesFound: ContentIssue[];
  knowledgeGaps: KnowledgeGapSuggestion[];
  reviewerNotes: string[];
  rawAnalysis: ContentAnalysis;
  evaluatedBy?: string;
  evaluatorName?: string;
  evaluatedAt: string;
  modelUsed?: string;
  createdAt: string;
  updatedAt: string;
  actions?: ImprovementAction[];
}

export interface SavedEvaluationResponse {
  success: boolean;
  data: SavedEvaluation | null;
  message?: string;
}

// Map issue types to suggested actions
export const ISSUE_TYPE_ACTIONS: Record<string, { action: ActionType; label: string; description: string }> = {
  TERMINOLOGY: {
    action: 'SEND_SUGGESTION',
    label: 'Suggest Fix',
    description: 'Send suggestion to super admins to modify generation prompts'
  },
  SPECIFICATION: {
    action: 'CREATE_DATA_ISSUE',
    label: 'Create Issue',
    description: 'Create data quality issue for investigation or AI research'
  },
  MISSING_CONTEXT: {
    action: 'SEND_SUGGESTION',
    label: 'Suggest Context',
    description: 'Send suggestion to add missing context'
  },
  EXPERTISE_SIGNAL: {
    action: 'SEND_SUGGESTION',
    label: 'Suggest Improvement',
    description: 'Send suggestion to improve expertise signals'
  },
  HALLUCINATION: {
    action: 'FLAG_BUG',
    label: 'Flag Bug',
    description: 'Flag as potential system bug for investigation'
  },
  LOGIC_ERROR: {
    action: 'FLAG_BUG',
    label: 'Flag Bug',
    description: 'Flag as potential logic error in generation'
  }
};

/**
 * Get the latest saved evaluation for a content item
 * Returns null if no evaluation has been saved yet
 */
export async function getSavedEvaluation(
  contentType: string,
  contentId: string
): Promise<SavedEvaluationResponse> {
  const response = await authFetch(`/content-evaluations/${contentType}/${contentId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get saved evaluation');
  }

  return response.json();
}

/**
 * Save an AI evaluation for persistence
 * This is called after analyzeContent to persist the results
 */
export async function saveEvaluation(
  contentType: string,
  contentId: string,
  siteId: string,
  analysis: ContentAnalysis,
  modelUsed?: string
): Promise<SavedEvaluationResponse> {
  const response = await authFetch(`/content-evaluations/${contentType}/${contentId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, analysis, modelUsed }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save evaluation');
  }

  return response.json();
}

/**
 * Take an action on an issue or gap within an evaluation
 * Actions include: SEND_SUGGESTION, CREATE_DATA_ISSUE, TRIGGER_RESEARCH, FLAG_BUG, DISMISS
 */
export async function takeIssueAction(
  evaluationId: string,
  itemType: 'issue' | 'gap',
  itemIndex: number,
  actionType: ActionType,
  issueData: ContentIssue | KnowledgeGapSuggestion,
  notes?: string
): Promise<{ success: boolean; action: ImprovementAction }> {
  // Use /actions/:evaluationId pattern to avoid route conflicts with /:contentType/:contentId
  const response = await authFetch(`/content-evaluations/actions/${evaluationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      itemType,
      itemIndex,
      actionType,
      issueData,
      notes
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to take action');
  }

  return response.json();
}

/**
 * Get the suggested action for an issue type
 */
export function getSuggestedActionForIssue(issueType: string): { action: ActionType; label: string; description: string } {
  return ISSUE_TYPE_ACTIONS[issueType] || {
    action: 'SEND_SUGGESTION',
    label: 'Send Suggestion',
    description: 'Send suggestion for review'
  };
}

/**
 * Check if an action has been taken for a specific issue/gap
 */
export function hasActionBeenTaken(
  actions: ImprovementAction[] | undefined,
  itemType: 'issue' | 'gap',
  itemIndex: number
): ImprovementAction | undefined {
  if (!actions) return undefined;
  return actions.find(a => a.itemType === itemType && a.itemIndex === itemIndex);
}

// ============================================
// Quality Evolution Tracking (Story 46)
// ============================================

export interface EvaluationHistoryItem {
  id: string;
  qualityScore: number;
  recommendation: string;
  issuesFound: ContentIssue[];
  evaluatedAt: string;
  evaluatorName?: string;
}

export interface EvaluationStats {
  totalEvaluations: number;
  averageScore: number;
  scoreImprovement: number;
  latestScore: number;
  firstScore: number;
}

export interface AllEvaluationsResponse {
  success: boolean;
  data: {
    evaluations: SavedEvaluation[];
    stats: EvaluationStats;
  };
}

export interface VersionHistoryItem {
  historyId: string;
  versionNumber: number;
  createdAt: string;
  evaluation: {
    id: string;
    qualityScore: number;
    recommendation: string;
    issuesCount: number;
    evaluatedAt: string;
  } | null;
}

export interface VersionHistoryResponse {
  success: boolean;
  data: VersionHistoryItem[];
}

export interface ResolutionResult {
  issueIndex: number;
  originalIssue: string;
  status: 'RESOLVED' | 'PARTIALLY_RESOLVED' | 'NOT_RESOLVED';
  explanation: string;
  evidence: string;
}

export interface ResolutionCheckResponse {
  success: boolean;
  data: {
    resolutions: ResolutionResult[];
    overallImprovement: string;
    remainingConcerns: string[];
    summary: {
      total: number;
      resolved: number;
      partiallyResolved: number;
      notResolved: number;
    };
    previousEvaluation: {
      id: string;
      qualityScore: number;
      evaluatedAt: string;
    };
  };
}

/**
 * Get all evaluations for a content item with quality evolution stats
 */
export async function getAllEvaluations(
  contentType: string,
  contentId: string
): Promise<AllEvaluationsResponse> {
  const response = await authFetch(`/content-evaluations/${contentType}/${contentId}/evaluations`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get evaluations');
  }

  return response.json();
}

/**
 * Get version history for a content item with linked evaluations
 */
export async function getVersionHistory(
  contentType: string,
  contentId: string,
  siteId: string
): Promise<VersionHistoryResponse> {
  const response = await authFetch(
    `/content-evaluations/${contentType}/${contentId}/version-history?siteId=${siteId}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get version history');
  }

  return response.json();
}

/**
 * Check if issues from a previous evaluation were resolved in new content
 * Uses LLM to compare new content against previous issues
 */
export async function checkIssueResolution(
  contentType: string,
  contentId: string,
  previousEvaluationId: string,
  newContent: Record<string, unknown>
): Promise<ResolutionCheckResponse> {
  const response = await authFetch(
    `/content-evaluations/${contentType}/${contentId}/check-resolution`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ previousEvaluationId, newContent }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check issue resolution');
  }

  return response.json();
}

/**
 * Get evaluation history (simpler version for detecting previous evaluations)
 */
export async function getEvaluationHistory(
  contentType: string,
  contentId: string,
  limit: number = 10
): Promise<{ success: boolean; data: EvaluationHistoryItem[] }> {
  const response = await authFetch(
    `/content-evaluations/${contentType}/${contentId}/history?limit=${limit}`
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get evaluation history');
  }

  return response.json();
}

// ============================================
// Hallucination Detection (Story 49)
// ============================================

export interface HallucinationDiscrepancy {
  attribute: string;
  claimed: string;
  actual: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  location?: string;
  difference?: string;
}

export interface HallucinationClaim {
  attribute: string;
  value: string;
  unit?: string;
  location?: string;
  confidence: number;
}

export interface HallucinationReport {
  id: string;
  generationId: string;
  productId: string;
  decision: 'PASS' | 'FLAG' | 'BLOCK';
  highestSeverity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  summary: string;
  stats: {
    totalClaims: number;
    verified: number;
    discrepancies: number;
    unverifiable: number;
  };
  discrepancies: HallucinationDiscrepancy[];
  verified?: Array<HallucinationClaim & { feedValue: string; matchType: string }>;
  unverifiable?: Array<HallucinationClaim & { reason: string }>;
  extractedClaims?: HallucinationClaim[];
  feedDataSnapshot?: Record<string, unknown>;
  checkedAt: string;
}

export interface HallucinationReportResponse {
  success: boolean;
  data: HallucinationReport | null;
  message?: string;
}

/**
 * Run hallucination validation on a generation
 * Compares generated content claims against source product feed data
 */
export async function validateSpecs(
  generationId: string
): Promise<HallucinationReportResponse> {
  const response = await authFetch(`/content-evaluations/validate-specs/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate specs');
  }

  return response.json();
}

/**
 * Get existing hallucination validation report for a generation
 */
export async function getHallucinationReport(
  generationId: string
): Promise<HallucinationReportResponse> {
  const response = await authFetch(`/content-evaluations/hallucination-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get hallucination report');
  }

  return response.json();
}

/**
 * Get severity badge color for hallucination discrepancies
 */
export function getHallucinationSeverityColor(severity: string): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    default:
      return 'default';
  }
}

/**
 * Get decision badge color for hallucination validation
 */
export function getHallucinationDecisionColor(decision: string): 'destructive' | 'warning' | 'default' {
  switch (decision) {
    case 'BLOCK':
      return 'destructive';
    case 'FLAG':
      return 'warning';
    case 'PASS':
      return 'default';
    default:
      return 'default';
  }
}

// ============================================
// Knowledge Coverage Scoring (Story 50)
// ============================================

export interface CoverageReport {
  id: string;
  generationId?: string;
  productId: string;
  siteId: string;
  category?: string;

  // Coverage metrics
  totalAttributes: number;
  knownAttributes: number;
  unknownAttributes: string[];
  knownAttributeNames: string[];
  coveragePercentage: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

  // Threshold info
  requiredThreshold?: number;
  belowThreshold: boolean;
  thresholdOverride: boolean;
  priceTier?: 'entry' | 'standard' | 'premium';

  // Metadata
  createdAt: string;
}

export interface CoverageReportResponse {
  success: boolean;
  data: CoverageReport | null;
  message?: string;
}

export interface KnowledgeGap {
  id: string;
  siteId: string;
  category?: string;
  attributeName: string;
  detectionCount: number;
  firstDetectedAt: string;
  lastDetectedAt: string;
  sampleProductIds: string[];
  queuedForResearch: boolean;
  researchPriority?: number;
}

export interface KnowledgeGapsResponse {
  success: boolean;
  data: KnowledgeGap[];
}

/**
 * Check knowledge coverage for a product before generation
 * Returns coverage analysis showing what % of attributes have domain knowledge
 */
export async function checkCoverage(
  productId: string,
  siteId: string,
  options?: { thresholdOverride?: boolean; logGaps?: boolean }
): Promise<CoverageReportResponse> {
  const response = await authFetch('/content-evaluations/check-coverage', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      productId,
      siteId,
      thresholdOverride: options?.thresholdOverride ?? false,
      logGaps: options?.logGaps ?? true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check coverage');
  }

  return response.json();
}

/**
 * Get stored coverage report for a generation
 */
export async function getCoverageReport(
  generationId: string
): Promise<CoverageReportResponse> {
  const response = await authFetch(`/content-evaluations/coverage-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get coverage report');
  }

  return response.json();
}

/**
 * Get top knowledge gaps for a site
 * Returns most frequently detected unknown attributes
 */
export async function getKnowledgeGaps(
  siteId: string,
  options?: { category?: string; limit?: number; queuedOnly?: boolean }
): Promise<KnowledgeGapsResponse> {
  const params = new URLSearchParams();
  params.set('siteId', siteId);
  if (options?.category) params.set('category', options.category);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.queuedOnly) params.set('queuedOnly', 'true');

  const response = await authFetch(`/content-evaluations/knowledge-gaps?${params}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get knowledge gaps');
  }

  return response.json();
}

/**
 * Get risk level badge color for coverage scoring
 */
export function getCoverageRiskColor(riskLevel: string): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (riskLevel) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get coverage percentage color based on thresholds
 */
export function getCoveragePercentageColor(percentage: number): string {
  if (percentage >= 75) return 'text-green-600';
  if (percentage >= 50) return 'text-yellow-600';
  if (percentage >= 25) return 'text-orange-600';
  return 'text-red-600';
}

// ============================================
// Persona Validation (Story 51)
// ============================================

export interface PersonaDetail {
  id: string;
  name: string;
  description: string;
}

export interface DetectedPersona {
  persona: string;
  personaName?: string;
  confidence: number;
  evidence: string[];
  impliedTier: 'entry' | 'mid_range' | 'premium' | 'ultra_premium' | 'unknown';
}

export interface PersonaValidationReport {
  id: string;
  generationId: string;
  productId: string;
  siteId: string;

  // Price tier info
  productPrice: number;
  priceTier: 'entry' | 'mid_range' | 'premium' | 'ultra_premium';
  priceTierDisplay: string;
  category?: string;
  validPersonas: PersonaDetail[];

  // Detection results
  detectedPersonas: DetectedPersona[];
  overallTone?: string;
  toneExplanation?: string;

  // Validation results
  alignmentScore: number;
  isAligned: boolean;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  mismatchReason?: string;
  recommendations: string[];

  validatedAt: string;
}

export interface PersonaValidationResponse {
  success: boolean;
  data: PersonaValidationReport | null;
  skipped?: boolean;
  reason?: string;
  message?: string;
}

export interface PersonaGuidance {
  promptAddition: string;
  toneGuidance: string;
  vocabularyGuidance: string[];
  avoidTerms: string[];
}

export interface PersonaTierInfo {
  tier: string;
  tierDisplayName: string;
  validPersonas: string[];
  validPersonaDetails: PersonaDetail[];
  priceRange: { min: number; max: number | null };
  category: string;
}

export interface PersonaGuidanceResponse {
  success: boolean;
  data: {
    tier: PersonaTierInfo;
    guidance: PersonaGuidance;
  } | null;
  message?: string;
}

/**
 * Run persona validation on a generation
 * Compares content targeting against product price tier
 */
export async function validatePersona(
  generationId: string
): Promise<PersonaValidationResponse> {
  const response = await authFetch(`/content-evaluations/validate-persona/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate persona');
  }

  return response.json();
}

/**
 * Get existing persona validation report for a generation
 */
export async function getPersonaReport(
  generationId: string
): Promise<PersonaValidationResponse> {
  const response = await authFetch(`/content-evaluations/persona-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get persona report');
  }

  return response.json();
}

/**
 * Get persona guidance for content generation
 */
export async function getPersonaGuidance(
  price: number,
  category?: string
): Promise<PersonaGuidanceResponse> {
  const response = await authFetch('/content-evaluations/persona-guidance', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ price, category }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get persona guidance');
  }

  return response.json();
}

/**
 * Get severity badge color for persona validation
 */
export function getPersonaSeverityColor(severity: string | null): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

/**
 * Get alignment status color
 */
export function getPersonaAlignmentColor(isAligned: boolean, score: number): string {
  if (isAligned && score >= 80) return 'text-green-600';
  if (isAligned) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
}

/**
 * Get tier badge variant
 */
export function getTierBadgeVariant(tier: string): 'default' | 'secondary' | 'outline' {
  switch (tier) {
    case 'ultra_premium':
    case 'premium':
      return 'default';
    case 'mid_range':
      return 'secondary';
    default:
      return 'outline';
  }
}

// ============================================
// Category Coherence Validation (Story 52)
// ============================================

export interface ExtractedAlternative {
  name: string;
  brand?: string;
  category: string;
  categoryDisplay?: string;
  context?: string;
  excerpt?: string;
}

export interface CoherenceIssue {
  alternative: string;
  brand?: string;
  category: string;
  reason: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  excerpt?: string;
}

export interface CoherenceValidationReport {
  id: string;
  generationId: string;
  productId: string;
  siteId: string;

  // Category info
  productCategory: string;
  productCategoryDisplay: string;
  discipline?: string;
  compatibleCategories: string[];

  // Alternatives
  extractedAlternatives: ExtractedAlternative[];
  compatibleAlternatives: ExtractedAlternative[];
  incompatibleAlternatives: ExtractedAlternative[];

  // Scoring
  coherenceScore: number | null;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  message: string;
  issues: CoherenceIssue[];

  validatedAt: string;
}

export interface CoherenceValidationResponse {
  success: boolean;
  data: CoherenceValidationReport | null;
  skipped?: boolean;
  reason?: string;
  message?: string;
}

/**
 * Run category coherence validation on a generation
 * Checks that alternatives are from compatible categories
 */
export async function validateCoherence(
  generationId: string
): Promise<CoherenceValidationResponse> {
  const response = await authFetch(`/content-evaluations/validate-coherence/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate coherence');
  }

  return response.json();
}

/**
 * Get existing coherence validation report for a generation
 */
export async function getCoherenceReport(
  generationId: string
): Promise<CoherenceValidationResponse> {
  const response = await authFetch(`/content-evaluations/coherence-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get coherence report');
  }

  return response.json();
}

/**
 * Get coherence score color based on value
 */
export function getCoherenceScoreColor(score: number | null): string {
  if (score === null) return 'text-gray-500';
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-yellow-600';
  if (score >= 40) return 'text-orange-600';
  return 'text-red-600';
}

/**
 * Get coherence severity badge color
 */
export function getCoherenceSeverityColor(severity: string | null): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

// ============================================
// Completeness Validation (Story 53)
// ============================================

export interface SpecRequirement {
  spec: string;
  displayName: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  description: string;
  aliases?: string[];
  conditional?: boolean;
  triggeredBy?: string;
}

export interface FoundSpec extends SpecRequirement {
  status: 'found';
  value: string;
  location?: string;
  confidence: number;
}

export interface MissingSpec extends SpecRequirement {
  status: 'missing';
}

export interface PartialSpec extends SpecRequirement {
  status: 'partial';
  foundValue: string;
  confidence: number;
}

export interface CompletenessValidationReport {
  id: string;
  generationId: string;
  productId: string;
  siteId: string;

  // Context
  category: string;
  priceTier: string;
  productAttributes: Record<string, boolean>;

  // Checklist
  requiredSpecs: SpecRequirement[];
  conditionalSpecs: SpecRequirement[];
  totalRequired: number;

  // Results
  foundSpecs: FoundSpec[];
  missingSpecs: MissingSpec[];
  partialSpecs: PartialSpec[];

  // Scoring
  completenessScore: number;
  weightedScore: number;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | null;
  decision: 'PASS' | 'FLAG' | 'BLOCK';
  missingCriticalCount: number;
  message: string;

  validatedAt: string;
}

export interface CompletenessValidationResponse {
  success: boolean;
  data: CompletenessValidationReport | null;
  skipped?: boolean;
  reason?: string;
  message?: string;
}

/**
 * Run completeness validation on a generation
 * Checks that content includes all required specs
 */
export async function validateCompleteness(
  generationId: string
): Promise<CompletenessValidationResponse> {
  const response = await authFetch(`/content-evaluations/validate-completeness/${generationId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to validate completeness');
  }

  return response.json();
}

/**
 * Get existing completeness validation report for a generation
 */
export async function getCompletenessReport(
  generationId: string
): Promise<CompletenessValidationResponse> {
  const response = await authFetch(`/content-evaluations/completeness-report/${generationId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get completeness report');
  }

  return response.json();
}

/**
 * Get completeness score color based on value
 */
export function getCompletenessScoreColor(score: number): string {
  if (score >= 90) return 'text-green-600';
  if (score >= 70) return 'text-yellow-600';
  if (score >= 50) return 'text-orange-600';
  return 'text-red-600';
}

/**
 * Get decision badge variant
 */
export function getCompletenessDecisionVariant(decision: string): 'default' | 'destructive' | 'warning' {
  switch (decision) {
    case 'PASS':
      return 'default';
    case 'FLAG':
      return 'warning';
    case 'BLOCK':
      return 'destructive';
    default:
      return 'default';
  }
}

/**
 * Get completeness severity badge color
 */
export function getCompletenessSeverityColor(severity: string | null): 'destructive' | 'warning' | 'secondary' | 'default' {
  switch (severity) {
    case 'CRITICAL':
      return 'destructive';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'secondary';
    case 'LOW':
      return 'default';
    default:
      return 'default';
  }
}

// ============================================
// Deployment Verification
// ============================================

export interface DeploymentVerificationResult {
  success: boolean;
  url?: string;
  productTitle?: string;
  faqStatus?: 'deployed' | 'partial' | 'modified' | 'not_found' | 'page_error' | 'js_required';
  faqSimilarity?: number;
  details?: {
    ourCount: number;
    liveCount: number;
    matchedCount: number;
    unmatchedCount: number;
    matches?: Array<{
      ourQuestion: string;
      liveQuestion: string;
      questionSimilarity: number;
      answerSimilarity: number;
      combinedSimilarity: number;
    }>;
    unmatchedQuestions?: string[];
    liveFaqSource?: string;
    checkedAt?: string;
  };
  error?: string;
  message?: string;
}

export interface DeploymentVerificationResponse {
  success: boolean;
  data: DeploymentVerificationResult;
}

export interface DeploymentVerificationStats {
  deployed: number;
  partial: number;
  modified: number;
  notFound: number;
  errors: number;
  jsRequired: number;
  totalVerified: number;
  pendingVerification: number;
  avgSimilarity: number;
  lastVerified: string | null;
}

export interface DeploymentStatsResponse {
  success: boolean;
  data: DeploymentVerificationStats;
}

/**
 * Verify deployment status for a single AEO item
 * Checks if the FAQs have been deployed to the live customer page
 */
export async function verifyDeployment(
  contentType: string,
  contentId: string
): Promise<DeploymentVerificationResponse> {
  const response = await authFetch(`/content-review/${contentType}/${contentId}/verify-deployment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to verify deployment');
  }

  return response.json();
}

/**
 * Get deployment verification statistics for a site
 */
export async function getDeploymentStats(
  siteId: string
): Promise<DeploymentStatsResponse> {
  const response = await authFetch(`/content-review/verification-stats/${siteId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get deployment stats');
  }

  return response.json();
}

/**
 * Get FAQ status badge color
 */
export function getFaqStatusColor(status: string): 'default' | 'destructive' | 'warning' | 'secondary' {
  switch (status) {
    case 'deployed':
      return 'default';
    case 'partial':
    case 'modified':
      return 'warning';
    case 'not_found':
    case 'page_error':
    case 'js_required':
      return 'destructive';
    default:
      return 'secondary';
  }
}

/**
 * Get FAQ status display label
 */
export function getFaqStatusLabel(status: string): string {
  switch (status) {
    case 'deployed':
      return 'Deployed';
    case 'partial':
      return 'Partially Deployed';
    case 'modified':
      return 'Modified';
    case 'not_found':
      return 'Not Found';
    case 'page_error':
      return 'Page Error';
    case 'js_required':
      return 'JS Required';
    default:
      return status;
  }
}
